'use client';

import { VisuallyHidden } from '@react-aria/visually-hidden';
import { useIntl } from 'react-intl';

import messages from '@/components/authForm/AppleAuthButton/messages';
import { useAuthPrompt } from '@/context/auth/AuthPromptProvider';
import { useAuth } from '@/context/auth/AuthProvider';
import { CalmAuthMethods } from '@/utils/privacyCookies';

import { useSisu } from '../Provider';
import { type MessageNode } from '../utils';
import { Icon, Wrapper } from './styles';

export type AppleAuthButtonProps = {
	/**
	 * @defaultValue
	 * ```tsx
	 * "sisu-apple-auth-button"
	 * ```
	 */
	'data-testid'?: string;

	/**
	 * @defaultValue
	 * ```tsx
	 * false
	 * ```
	 */
	hideMessage?: boolean;

	message?: MessageNode;
};

export default function AppleAuthButton({
	'data-testid': testId = 'sisu-apple-auth-button',
	hideMessage = false,
	message,
}: AppleAuthButtonProps) {
	const { authenticate } = useAuth();
	const { formatMessage } = useIntl();
	const { authPrompt } = useAuthPrompt();
	const { isMarketingOptInSelected } = useSisu();

	const handleClick = () => {
		authenticate({ authMethod: CalmAuthMethods.Apple, marketingOptIn: isMarketingOptInSelected });
	};

	if (!message) {
		message = formatMessage(authPrompt === 'login' ? messages.appleSignInCta : messages.appleContinueCta);
	}

	return (
		<Wrapper
			backgroundColor="white"
			contentAlignment="center"
			onPress={handleClick}
			data-testid={testId}
			role="link"
			$noPadding={hideMessage}
			textColor="black"
			borderColor="gray3"
		>
			<Icon $noMargin={hideMessage} $iconColor="black" />
			{hideMessage ? <VisuallyHidden>{message}</VisuallyHidden> : message}
		</Wrapper>
	);
}
